import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Layout } from './Layout';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/store/session';

const Home = lazy(() => import('@/pages/Home'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const GoogleSignup = lazy(() => import('@/pages/GoogleSignup'));
const Profile = lazy(() => import('@/pages/Profile'));
const Leaderboard = lazy(() => import('@/pages/Leaderboard'));
const Settings = lazy(() => import('@/pages/Settings'));
const AvatarEditor = lazy(() => import('@/pages/AvatarEditor'));
const BoardLab = lazy(() => import('@/pages/BoardLab'));
const Play = lazy(() => import('@/pages/Play'));
const Game = lazy(() => import('@/pages/Game'));
const Tv = lazy(() => import('@/pages/Tv'));
const Analysis = lazy(() => import('@/pages/Analysis'));
const Studies = lazy(() => import('@/pages/Studies'));
const Study = lazy(() => import('@/pages/Study'));
const Puzzles = lazy(() => import('@/pages/Puzzles'));
const Tournaments = lazy(() => import('@/pages/Tournaments'));
const Challenges = lazy(() => import('@/pages/Challenges'));
const Challenge = lazy(() => import('@/pages/Challenge'));
const Guardian = lazy(() => import('@/pages/Guardian'));
const Editor = lazy(() => import('@/pages/Editor'));
const Pairings = lazy(() => import('@/pages/Pairings'));
const ChessClock = lazy(() => import('@/pages/ChessClock'));
const Coordinates = lazy(() => import('@/pages/Coordinates'));
const ZeroHero = lazy(() => import('@/pages/ZeroHero'));
const ProTactics = lazy(() => import('@/pages/ProTactics'));
const ProEndgames = lazy(() => import('@/pages/ProEndgames'));
const StudyTrainer = lazy(() => import('@/pages/StudyTrainer'));
const Openings = lazy(() => import('@/pages/Openings'));
const Tournament = lazy(() => import('@/pages/Tournament'));
const Communities = lazy(() => import('@/pages/Communities'));
const Community = lazy(() => import('@/pages/Community'));
const ClubInvite = lazy(() => import('@/pages/ClubInvite'));
const Forum = lazy(() => import('@/pages/Forum'));
const Blog = lazy(() => import('@/pages/Blog'));
const Simuls = lazy(() => import('@/pages/Simuls'));
const Simul = lazy(() => import('@/pages/Simul'));
const Moderation = lazy(() => import('@/pages/Moderation'));
const Inbox = lazy(() => import('@/pages/Inbox'));
const Race = lazy(() => import('@/pages/Race'));
const GameSearch = lazy(() => import('@/pages/GameSearch'));
const Soon = lazy(() => import('@/pages/Soon'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Legal = lazy(() => import('@/pages/Legal'));

export function App() {
  const ready = useSession((s) => s.ready);

  // Hasta saber si hay sesión no se pinta nada: evita que la interfaz
  // aparezca como «no conectado» y cambie medio segundo después.
  if (!ready) {
    return (
      <div className="flex h-dvh items-center justify-center bg-bg">
        <Spinner label="Cargando UAChess" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path="/*"
          element={
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/entrar" element={<Login />} />
                <Route path="/registro" element={<Register />} />
                <Route path="/registro/google" element={<GoogleSignup />} />
                <Route path="/ajustes" element={<Settings />} />
                <Route path="/avatar" element={<AvatarEditor />} />
                <Route path="/clasificacion" element={<Leaderboard />} />
                <Route path="/tablero" element={<BoardLab />} />
                <Route path="/@/:username" element={<Profile />} />

                {/* Secciones en construcción; cada una llega en su turno. */}
                <Route path="/jugar" element={<Play />} />
                <Route path="/partida/:id" element={<Game />} />
                <Route path="/tv" element={<Tv />} />
                <Route path="/analisis" element={<Analysis />} />
                <Route path="/estudios" element={<Studies />} />
                <Route path="/estudio/:id" element={<Study />} />
                <Route path="/estudio/:id/entrenar" element={<StudyTrainer />} />
                <Route path="/estudio/:id/:chapterId" element={<Study />} />
                <Route path="/tactica" element={<Puzzles />} />
                <Route path="/torneos" element={<Tournaments />} />
                <Route path="/torneo/:id" element={<Tournament />} />
                <Route path="/comunidades" element={<Communities />} />
                <Route path="/comunidad/:slug" element={<Community />} />
                <Route path="/club/unirse/:code" element={<ClubInvite />} />
                <Route path="/desafios" element={<Challenges />} />
                <Route path="/reto/:id" element={<Challenge />} />
                <Route path="/guardian" element={<Guardian />} />
                <Route path="/moderacion" element={<Moderation />} />
                <Route path="/mensajes" element={<Inbox />} />
                <Route path="/mensajes/:username" element={<Inbox />} />
                <Route path="/moderacion/:username" element={<Moderation />} />

                {/* Secciones del menú todavía por construir: cada una enseña qué
                    llevará, en vez de dar un enlace roto. */}
                <Route path="/simultaneas" element={<Simuls />} />
                <Route path="/simultanea/:id" element={<Simul />} />
                <Route path="/carrera" element={<Race />} />
                <Route path="/carrera/:id" element={<Race />} />
                <Route path="/fundamentos/*" element={<Navigate to="/aprender/de-zero-a-hero" replace />} />
                <Route path="/aprender/de-zero-a-hero" element={<ZeroHero />} />
                <Route path="/aprender/de-zero-a-hero/:nivel" element={<ZeroHero />} />
                <Route path="/aprender/de-zero-a-hero/:nivel/:leccion" element={<ZeroHero />} />
                <Route path="/aprender/pro-tactics" element={<ProTactics />} />
                <Route path="/aprender/pro-endgames" element={<ProEndgames />} />
                <Route path="/practicar" element={<Soon section="practicar" />} />
                <Route path="/coordenadas" element={<Coordinates />} />
                <Route path="/entrenadores" element={<Soon section="entrenadores" />} />
                <Route path="/retransmisiones" element={<Soon section="retransmisiones" />} />
                <Route path="/videos" element={<Soon section="videos" />} />
                <Route path="/foro/*" element={<Forum />} />
                <Route path="/blog/*" element={<Blog />} />
                <Route path="/aperturas" element={<Openings />} />
                <Route path="/editor" element={<Editor />} />
                <Route path="/pareo" element={<Pairings />} />
                <Route path="/reloj" element={<ChessClock />} />
                <Route path="/busqueda" element={<GameSearch />} />
                <Route path="/terminos" element={<Legal page="terms" />} />
                <Route path="/ayuda" element={<Legal page="faq" />} />

                {/* Atajo cómodo: /MagnusUA lleva al perfil. */}
                <Route path="/perfil" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner />
    </div>
  );
}
